/**
 * TCB R36 B2B procurement runtime adapter.
 * Extends canonical company/assortment/reorder JSON — not a parallel CRM/ERP/quote engine.
 */
(function (global) {
  'use strict';

  var CFG = global.TCB_B2B_PROCUREMENT || null;
  var MAX_SKUS = 8;

  function loadConfig(cb) {
    if (!CFG && global.TCB_B2B_PROCUREMENT) CFG = global.TCB_B2B_PROCUREMENT;
    if (CFG) {
      cb(null, CFG);
      return;
    }
    var node = document.querySelector('[data-tcb-b2b-runtime-url]');
    var url = node && node.getAttribute('data-tcb-b2b-runtime-url');
    if (!url || !global.fetch) {
      cb(new Error('b2b_runtime_config_missing'));
      return;
    }
    global
      .fetch(url)
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        CFG = json;
        global.TCB_B2B_PROCUREMENT = json;
        cb(null, json);
      })
      .catch(function (err) {
        cb(err || new Error('b2b_runtime_fetch_failed'));
      });
  }

  function assertTenant(companyId, resourceCompanyId) {
    if (!companyId || !resourceCompanyId || companyId !== resourceCompanyId) {
      return { ok: false, error: 'tenant_isolation_violation' };
    }
    return { ok: true };
  }

  function getCompany(companyId) {
    if (!CFG) return null;
    return (CFG.companies || []).find(function (c) {
      return c.company_id === companyId;
    }) || null;
  }

  function getLocations(companyId) {
    return ((CFG && CFG.locations) || []).filter(function (l) {
      return l.company_id === companyId;
    });
  }

  function getContacts(companyId) {
    return ((CFG && CFG.contacts) || []).filter(function (c) {
      return c.company_id === companyId;
    });
  }

  function contactMay(contact, permission) {
    if (!contact || contact.status !== 'ACTIVE') return false;
    return (contact.permissions || []).indexOf(permission) !== -1;
  }

  function resolveAssortment(companyId, locationId) {
    var list = ((CFG && CFG.assortments) || []).filter(function (a) {
      return a.company_id === companyId && a.status === 'ACTIVE';
    });
    var exact = list.find(function (a) {
      return (a.location_ids || []).indexOf(locationId) !== -1;
    });
    if (exact) return exact;
    return (
      list.find(function (a) {
        return (a.location_ids || []).indexOf('*') !== -1;
      }) || null
    );
  }

  function packageEligible(companyId, locationId, packageId, skuCount) {
    var company = getCompany(companyId);
    if (!company || company.account_status !== 'ACTIVE') {
      return { ok: false, reason: 'company_not_active' };
    }
    if ((skuCount || 0) > MAX_SKUS) {
      return { ok: false, reason: 'sku_cap_exceeded', max_skus: MAX_SKUS };
    }
    var assortment = resolveAssortment(companyId, locationId);
    if (!assortment) {
      return { ok: false, reason: 'no_active_assortment' };
    }
    if ((assortment.excluded_package_ids || []).indexOf(packageId) !== -1) {
      return { ok: false, reason: 'package_excluded' };
    }
    if ((assortment.package_ids || []).indexOf(packageId) === -1) {
      return { ok: false, reason: 'package_not_in_approved_list' };
    }
    return {
      ok: true,
      assortment_id: assortment.assortment_id,
      purchase_mode_hint: 'direct_cart_or_quote_per_package_gates'
    };
  }

  function revalidateReorder(templateId, context) {
    context = context || {};
    var template = ((CFG && CFG.reorder_templates) || []).find(function (t) {
      return t.template_id === templateId;
    });
    if (!template) return { ok: false, status: 'invalid', reasons: ['unknown_template'] };

    var tenant = assertTenant(context.company_id || template.company_id, template.company_id);
    if (!tenant.ok) return { ok: false, status: 'invalid', reasons: [tenant.error] };

    var reasons = [];
    var warnings = [];
    var status = 'valid';

    if (template.status !== 'ACTIVE') {
      status = 'invalid';
      reasons.push('template_not_active');
    }

    (template.lines || []).forEach(function (line) {
      if (line.kind === 'package') {
        var elig = packageEligible(template.company_id, template.location_id, line.package_id, line.unique_sku_count || 0);
        if (!elig.ok) {
          status = 'invalid';
          reasons.push('package_ineligible:' + elig.reason);
        }
        if ((line.unique_sku_count || 0) > MAX_SKUS) {
          status = 'invalid';
          reasons.push('sku_cap_exceeded');
        }
      }
    });

    // R34 availability adapter contract — unknown never silently available
    warnings.push('availability_recheck_required');
    warnings.push('price_recheck_required');
    if (status === 'valid') status = 'review_required';

    // Optional compatibility runtime if present
    if (global.TCBCompatibilityRuntime && typeof global.TCBCompatibilityRuntime.evaluate === 'function') {
      (template.lines || []).forEach(function (line) {
        if (line.kind === 'package' && line.package_id) {
          var compat = global.TCBCompatibilityRuntime.evaluate(line.package_id, context);
          if (compat && compat.status === 'invalid') {
            status = 'invalid';
            reasons = reasons.concat(compat.reasons || ['compatibility_invalid']);
          } else if (compat && compat.status === 'review_required') {
            if (status === 'valid') status = 'review_required';
            warnings = warnings.concat(compat.warnings || ['compatibility_review']);
          }
        }
      });
    }

    return {
      ok: status === 'valid',
      status: status,
      template_id: templateId,
      company_id: template.company_id,
      location_id: template.location_id,
      reasons: reasons,
      warnings: warnings,
      revalidation: template.revalidation_required || [],
      engine: 'tcb-b2b-procurement-runtime',
      version: (CFG && CFG.version) || 'r36'
    };
  }

  function buildQuoteContext(companyId, locationId, contactId, packageId) {
    return {
      company_id: companyId,
      location_id: locationId,
      contact_id: contactId,
      package_id: packageId,
      channel: 'b2b_account_hub',
      draft_order_adapter: !!(CFG && CFG.quote_continuity && CFG.quote_continuity.policy && CFG.quote_continuity.policy.draft_order_adapter)
    };
  }

  function track(eventName, payload) {
    var detail = Object.assign({ event: eventName, at: new Date().toISOString() }, payload || {});
    try {
      if (global.TCBAnalytics && typeof global.TCBAnalytics.track === 'function') {
        global.TCBAnalytics.track(eventName, detail);
      }
      global.dispatchEvent(new CustomEvent('tcb:b2b', { detail: detail }));
    } catch (e) {
      /* no-op */
    }
  }

  var api = {
    version: 'r36.1.0',
    maxSkus: MAX_SKUS,
    loadConfig: loadConfig,
    getCompany: getCompany,
    getLocations: getLocations,
    getContacts: getContacts,
    contactMay: contactMay,
    resolveAssortment: resolveAssortment,
    packageEligible: packageEligible,
    revalidateReorder: revalidateReorder,
    buildQuoteContext: buildQuoteContext,
    assertTenant: assertTenant,
    track: track
  };

  global.TCBB2BProcurementRuntime = api;
})(typeof window !== 'undefined' ? window : globalThis);
